import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';

import { Database, withLogs } from '@core';

export type SendMessagePayload = {
    id?: number;
    threadId: number;
    message: string;
    createdAt?: string;
    updatedAt?: string;
    createdBy: string;
    authorName: string;
};

export type DeleteMessagePayload = {
    threadId: number;
    id: number;
};

export type SwingThreadMessage = {
    id: number;
    threadId: number;
    message: string;
    createdAt: string;
    createdBy: string;
    updatedAt: string | null;
    isDeleted: boolean;
    authorName: string | null;
};

export const readSwingThreadMessages = withLogs(
    async (supabaseClient: SupabaseClient<Database>, threadId: number): Promise<SwingThreadMessage[]> => {
        if (!threadId) {
            throw new Error('threadId is required');
        }
        const { data, error } = await supabaseClient
            .from('swing_thread_messages')
            .select('*')
            .eq('thread_id', threadId)
            .or('is_deleted.is.false,is_deleted.is.null')
            .order('created_at', { ascending: true });

        if (error) {
            throw new Error(`Failed to read swing thread messages: ${error.message}`);
        }

        return data ? camelcaseKeys(data) : [];
    },
    'readSwingThreadMessages',
);

export const sendSwingThreadMessage = withLogs(
    async (supabaseClient: SupabaseClient<Database>, payload: SendMessagePayload): Promise<SwingThreadMessage> => {
        if (!payload.threadId) throw new Error('threadId is required');
        if (!payload.message) throw new Error('message is required');
        if (payload.id) {
            payload.updatedAt = new Date().toISOString();
        }
        if (!payload.createdBy) {
            throw new Error('createdBy is missing');
        }
        const { data, error } = await supabaseClient
            .from('swing_thread_messages')
            .upsert(snakecaseKeys(payload))
            .select('*')
            .single();

        if (error) {
            throw new Error(`Failed to send swing thread message: ${error.message}`);
        }

        return camelcaseKeys(data);
    },
    'sendSwingThreadMessage',
);

export const deleteSwingThreadMessage = withLogs(
    async (
        supabaseClient: SupabaseClient<Database>,
        { threadId, id }: DeleteMessagePayload,
    ): Promise<{ ok: boolean }> => {
        const { error: deleteError } = await supabaseClient
            .from('swing_thread_messages')
            .update({ is_deleted: true })
            .eq('thread_id', threadId)
            .eq('id', id);

        if (deleteError) throw deleteError;

        return { ok: true };
    },
    'sendSwingThreadMessage',
);

export const subscribeToSwingThreadMessages = (
    supabaseClient: SupabaseClient<Database>,
    threadId: number,
    handler: (payload: any) => Promise<void>,
) => {
    return supabaseClient
        .channel('swing_thread_messages_channel')
        .on(
            'postgres_changes',
            {
                event: '*',
                schema: 'public',
                table: 'swing_thread_messages',
                filter: `thread_id=eq.${threadId}`,
            },
            async (payload) => {
                handler(payload);
            },
        )
        .subscribe((status) => {
            if (status === 'CHANNEL_ERROR') {
                console.error('Swing thread messages subscription error:', status);
            }
        });
};

/*
TODO 
 * IF WE ENCRYPT MESSAGES THEN USE THIS
 * IF NOT REMOVE
export const sendSwingThreadMessage = withLogs(
    async (supabaseClient: SupabaseClient<Database>, payload: SendMessagePayload): Promise<{ ok: boolean }> => {
        const { data, error } = await supabaseClient.functions.invoke('swing-thread-send-message', {
            body: snakecaseKeys(payload),
            method: 'POST',
        });

        if (error) throw error;
        return data;
    },
    'sendSwingThreadMessage',
);

export const readSwingThreadMessages = withLogs(
    async (supabaseClient: SupabaseClient<Database>, threadId: number): Promise<SwingThreadMessage[]> => {
        if (!threadId) {
            throw new Error('threadId is required');
        }

        const { data, error } = await supabaseClient.functions.invoke(
            `swing-thread-read-message?threadId=${threadId}`,
            {
                method: 'GET',
            },
        );

        if (error) {
            throw new Error(`Failed to read swing thread messages: ${error.message}`);
        }

        return data ?? [];
    },
    'readSwingThreadMessages',
);
*/
